import type { Edge, Node } from '@xyflow/react';

// ── Graph editor types (existing) ──────────────────────────────────────────

export type StepNodeType =
  | 'pan_verification'
  | 'aadhaar_verification'
  | 'face_match'
  | 'liveness'
  | 'bank_account_verification'
  | 'kra_check'
  | 'document_upload'
  | 'esign_agreement';

export type WorkflowNodeData = {
  label: string;
  stepType: StepNodeType;
};

export type WorkflowNode = Node<WorkflowNodeData, 'stepNode'>;
export type WorkflowEdge = Edge;

export const STEP_NODE_META: Record<StepNodeType, { label: string; description: string }> = {
  pan_verification: {
    label: 'PAN Verification',
    description: 'Validate PAN against Income Tax database',
  },
  aadhaar_verification: {
    label: 'Aadhaar Verification',
    description: 'Verify Aadhaar via DigiLocker, Secure QR, or OCR',
  },
  face_match: {
    label: 'Face Match',
    description: 'Match live selfie against Aadhaar photo',
  },
  liveness: {
    label: 'Liveness',
    description: 'Confirm applicant is a live person',
  },
  bank_account_verification: {
    label: 'Bank Account Verification',
    description: 'Confirm account is active and name matches',
  },
  kra_check: {
    label: 'KRA Check',
    description: 'KRA registry check (scaffold)',
  },
  document_upload: {
    label: 'Document Upload',
    description: 'Auto-generate agreement from template',
  },
  esign_agreement: {
    label: 'eSign Agreement',
    description: 'Collect electronic signature (scaffold)',
  },
};

export const SEBI_STEP_ORDER: StepNodeType[] = [
  'pan_verification',
  'aadhaar_verification',
  'face_match',
  'liveness',
  'bank_account_verification',
  'kra_check',
  'document_upload',
  'esign_agreement',
];

// ── Simple editor step type metadata ───────────────────────────────────────

export type StepType =
  | 'pan_verification'
  | 'aadhaar_otp'
  | 'face_match'
  | 'liveness'
  | 'bank_account'
  | 'id_verification'
  | 'document_upload'
  | 'esign'
  | 'kra_check'
  | 'proof_of_address'
  | 'credit_bureau'
  | 'utility_bill'
  | 'ration_card'
  | 'msme'
  | 'gstin';

export type StepMeta = {
  label: string;
  description: string;
  icon: string;
  baseCost: number | null;
  freeQuota?: number;
};

export const STEP_TYPE_META: Record<string, StepMeta> = {
  pan_verification: {
    label: 'PAN Verification',
    description: 'Validate PAN against Income Tax database',
    icon: 'FileText',
    baseCost: 0.08,
  },
  aadhaar_otp: {
    label: 'Aadhaar Verification',
    description: 'Verify Aadhaar via DigiLocker, Secure QR, or OCR',
    icon: 'Smartphone',
    baseCost: 0.1,
  },
  face_match: {
    label: 'Face Match',
    description: 'Match live selfie against ID photo',
    icon: 'ScanFace',
    baseCost: 0.05,
  },
  liveness: {
    label: 'Face Liveness',
    description: 'Confirm applicant is live person',
    icon: 'Eye',
    baseCost: 0.15,
  },
  bank_account: {
    label: 'Bank Account Verification',
    description: 'Confirm account is active and name matches',
    icon: 'Landmark',
    baseCost: 0.12,
  },
  id_verification: {
    label: 'ID Verification',
    description: 'Verify a Voter ID, Passport, or Driving Licence',
    icon: 'CreditCard',
    baseCost: 0.05,
  },
  document_upload: {
    label: 'Document Upload',
    description: 'Upload and process documents',
    icon: 'Upload',
    baseCost: 0.08,
  },
  esign: {
    label: 'eSign Agreement',
    description: 'Collect electronic signature',
    icon: 'PenLine',
    baseCost: 0.2,
  },
  kra_check: {
    label: 'KRA Check',
    description: 'KRA registry check',
    icon: 'Database',
    baseCost: 0.06,
  },
  proof_of_address: {
    label: 'Proof of Address',
    description: 'Address verification from document',
    icon: 'MapPin',
    baseCost: 0.08,
  },
  credit_bureau: {
    label: 'Credit Bureau',
    description: 'Experian credit bureau soft pull',
    icon: 'TrendingUp',
    baseCost: 0.18,
  },
  gstin: {
    label: 'GSTIN Verification',
    description: 'GST identification number check',
    icon: 'Building2',
    baseCost: 0.05,
  },
  utility_bill: {
    label: 'Utility Bill Verification',
    description: 'Verify an Electricity, PNG, or LPG connection as proof of address',
    icon: 'Zap',
    baseCost: 0.05,
  },
  ration_card: {
    label: 'Ration Card Verification',
    description: 'Verify a ration card as address / household proof',
    icon: 'FileText',
    baseCost: 0.05,
  },
  msme: {
    label: 'MSME Verification (Udyam)',
    description: "Verify an enterprise's Udyam registration + profile / financials",
    icon: 'Building2',
    baseCost: 0.05,
  },
  esign_template: {
    label: 'eSign Document Template',
    description:
      'Sign the document generated by the previous step — set each signer and where they sign',
    icon: 'PenLine',
    // Billed PER SIGNER, by each signer's method — so the real cost is computed
    // from the signers placed on the document, not from this number. Only a
    // fallback for a step with no signers configured yet.
    baseCost: 0,
  },
  questionnaire: {
    label: 'Questionnaire',
    description: 'Ask the applicant a set of questions and record their answers',
    icon: 'ClipboardList',
    // FREE. This step calls no provider — it renders a form and stores the answers in our own
    // database — so there is nothing to bill and no catalogue entry behind it. `null`, not 0:
    // zero reads as "a priced call that happens to cost nothing", which the cost breakdown would
    // then show as a ₹0.00 line item.
    baseCost: null,
  },
  email_verification: {
    label: 'Email Verification',
    description:
      'Email the applicant a code and have them enter it, proving they control the mailbox',
    icon: 'Mail',
    // FREE, and free for a different reason than Questionnaire: this step DOES send something, but
    // WE send it — through Studio's own SMTP, the same credentials that already carry applicant
    // invites — so no verification provider is called and nothing is billed to an external API.
    //
    // `null` rather than 0 for the same reason as Questionnaire: 0 reads as "a priced call that
    // happens to cost nothing" and would show as a ₹0.00 line in the cost breakdown.
    //
    // WHEN THIS SHOULD BE CHARGED: give it a Console ExternalAPIType and a catalogue row, and the
    // existing per-tenant pricing sync handles the rest with no change here. Deliberately NOT
    // pointed at Console's Email_Verification = 29 (Protean) — that API checks whether an address
    // is real and deliverable, which is a different claim from proving someone reads it, and
    // borrowing its type would bill Protean usage for calls that never happened.
    baseCost: null,
  },
  document_template: {
    label: 'Document Template',
    description: 'Generate a Word/PDF document from a template and collect signatures',
    icon: 'FileText',
    // Generating the document IS a billed call (Console ExternalAPIType.Template = 9),
    // charged to the same wallet as every other step — so it belongs in the session
    // cost. This is only the pre-catalogue fallback; the real per-tenant price comes
    // from the catalogue entry, keyed by the "Template Management API" tag below.
    baseCost: 10,
  },
};

export const ALL_STEP_TYPES: string[] = Object.keys(STEP_TYPE_META);

// Maps each step type to its primary API catalogue tag.
// Used by the Method tab to filter the catalogue to relevant APIs for that step.
export const STEP_TYPE_CATALOGUE_TAGS: Record<string, string> = {
  pan_verification: 'PAN Verification',
  aadhaar_otp: 'Aadhaar Verification',
  aadhaar_verification: 'Aadhaar Verification',
  face_match: 'Face Match',
  liveness: 'Face Liveness',
  bank_account: 'Bank Account Verification',
  bank_account_verification: 'Bank Account Verification',
  esign: 'eSign API Suite',
  esign_agreement: 'eSign API Suite',
  document_upload: 'Template Management API',
  document_template: 'Template Management API',
  esign_template: 'eSign API Suite',
  kra_check: 'CKYC Verification',
  gstin: 'GST Verification',
  id_verification: 'ID Verification',
  utility_bill: 'Utility Bill Verification',
  ration_card: 'Ration Card Verification',
  msme: 'MSME Verification (Udyam)',
  credit_bureau: 'Experian Credit Bureau (Retail) - Soft Pull',
  proof_of_address: 'Property Tax Verification',
};

export const SEBI_STEP_TYPES: string[] = [
  'id_verification',
  'aadhaar_otp',
  'pan_verification',
  'liveness',
  'face_match',
  'bank_account',
  'esign',
];
